import { useEffect, useRef, useState } from 'react';
import {
  CHECKLIST_CATALOG_IMPACT_POLICY,
  CHECKLIST_CATALOG_IMPACT_ZIP_LIMITS as ZIP_LIMITS,
  type CatalogDeliveryPrepareDto,
  type ChecklistCatalogDeliveryManifest,
  type ChecklistCatalogImpactReportDto,
} from '@tcvs/shared';
import { useAuth } from '../auth/AuthContext';
import type { ApiResult } from '../auth/api-client';
import { useCatalogDeliveryApi, uploadCatalogDeliveryFile } from './catalog-delivery-api';
import {
  CatalogFileError,
  inspectCatalogDeliveryFiles,
  type LocalCatalogProduct,
} from './catalog-delivery-zip';
import {
  downloadCatalogImpactReports,
  verifyCatalogImpactReportDto,
} from './catalog-delivery-report';

class DeliveryRequestError extends Error {}
function data<T>(result: ApiResult<T>): T {
  if (result.ok) return result.data;
  throw new DeliveryRequestError(
    result.status === 429
      ? 'The staging allowance is exhausted. Stop and resume later; requests are not retried automatically.'
      : result.status === 410
        ? 'This delivery expired. Select the files again to start a new delivery.'
        : result.status === 403
          ? 'This session or source target is not authorized for catalog staging.'
          : 'The request failed. Progress is retained; explicitly resume when ready.',
  );
}
interface ProductProgress {
  uploaded: boolean;
  sealed: boolean;
  requestId: string;
}

export function CatalogDeliveryPreview(): JSX.Element {
  const api = useCatalogDeliveryApi();
  const auth = useAuth();
  const accessToken = auth.getAccessToken();
  const [files, setFiles] = useState<File[]>([]);
  const [products, setProducts] = useState<LocalCatalogProduct[]>([]);
  const [reports, setReports] = useState<ChecklistCatalogImpactReportDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState('');
  const [inputVersion, setInputVersion] = useState(0);
  const generation = useRef(0);
  const manifest = useRef<ChecklistCatalogDeliveryManifest | null>(null);
  const prepared = useRef<CatalogDeliveryPrepareDto | null>(null);
  const progress = useRef(new Map<string, ProductProgress>());
  const clear = (resetInput = true) => {
    generation.current += 1;
    manifest.current = null;
    prepared.current = null;
    progress.current.clear();
    setFiles([]);
    setProducts([]);
    setReports([]);
    setBusy(false);
    setReady(false);
    setError(null);
    setPhase('');
    if (resetInput) setInputVersion((version) => version + 1);
  };
  useEffect(() => {
    clear();
    return () => {
      generation.current += 1;
    };
  }, [auth.claims?.sub, accessToken]);
  const prepare = async () => {
    if (busy || !files.length) return;
    const active = ++generation.current;
    const current = () => active === generation.current;
    setBusy(true);
    setError(null);
    setPhase('Checking local files');
    try {
      if (!manifest.current) {
        const local = await inspectCatalogDeliveryFiles(files, current);
        if (!current()) return;
        setProducts(local);
        const entries: ChecklistCatalogDeliveryManifest['products'] = [];
        for (const product of local) {
          setPhase(`Reading baseline for target ${product.sourceTargetId}`);
          const baseline = data(await api.baseline(product.sourceTargetId));
          if (!current()) return;
          if (
            baseline.sourceTargetId !== product.sourceTargetId ||
            baseline.policyVersion !== CHECKLIST_CATALOG_IMPACT_POLICY ||
            !baseline.basedOn ||
            baseline.coverage.catalog !== 'COMPLETE' ||
            baseline.coverage.corrections !== 'COMPLETE'
          )
            throw new DeliveryRequestError(
              `Target ${product.sourceTargetId} has no complete bounded baseline. Nothing was staged; resolve the baseline before retrying.`,
            );
          entries.push({
            sourceTargetId: product.sourceTargetId,
            file: product.file,
            mode: 'ordinary',
            contentEncoding: 'identity',
            transportSha256: product.sha256,
            transportBytes: product.bytes.length,
            rawSha256: product.sha256,
            rawBytes: product.bytes.length,
            expected: product.expected,
            basedOn: baseline.basedOn,
            changeKeys: [],
          });
        }
        manifest.current = {
          schemaVersion: 1,
          deliveryId: crypto.randomUUID(),
          sourceSystem: 'azure-sql-ebay',
          createdAt: new Date().toISOString(),
          products: entries,
        };
      }
      setPhase('Preparing immutable delivery');
      const result = data(await api.prepare(manifest.current));
      if (!current()) return;
      prepared.current = result;
      setReady(true);
      setPhase('Ready for one-product validation');
    } catch (cause) {
      if (current())
        setError(
          cause instanceof CatalogFileError || cause instanceof DeliveryRequestError
            ? cause.message
            : 'Could not prepare this delivery. Check the files and connection, then explicitly resume.',
        );
    } finally {
      if (current()) setBusy(false);
    }
  };
  const next = async () => {
    if (busy || !prepared.current || !manifest.current) return;
    const product = products.find(
      (item) => !reports.some((report) => report.report.sourceTargetId === item.sourceTargetId),
    );
    if (!product) return;
    const active = ++generation.current,
      current = () => active === generation.current;
    setBusy(true);
    setError(null);
    try {
      const delivery = manifest.current;
      let state = progress.current.get(product.sourceTargetId);
      if (!state) {
        state = {
          uploaded: false,
          sealed: prepared.current.products.some(
            (item) => item.sourceTargetId === product.sourceTargetId && item.status === 'SEALED',
          ),
          requestId: crypto.randomUUID(),
        };
        progress.current.set(product.sourceTargetId, state);
      }
      if (!state.sealed) {
        if (!state.uploaded) {
          let upload = prepared.current.uploads.find(
            (item) => item.sourceTargetId === product.sourceTargetId,
          );
          if (
            !upload ||
            !Number.isFinite(Date.parse(upload.expiresAt)) ||
            Date.parse(upload.expiresAt) <= Date.now()
          ) {
            const refreshed = data(await api.prepare(delivery));
            if (!current()) return;
            prepared.current = refreshed;
            state.sealed = refreshed.products.some(
              (item) => item.sourceTargetId === product.sourceTargetId && item.status === 'SEALED',
            );
            upload = refreshed.uploads.find(
              (item) => item.sourceTargetId === product.sourceTargetId,
            );
          }
          if (!state.sealed) {
            if (!upload)
              throw new DeliveryRequestError(
                'No upload slot is available. This delivery is retained; explicitly resume later.',
              );
            setPhase(`Uploading target ${product.sourceTargetId}`);
            await uploadCatalogDeliveryFile(upload, product.bytes);
            if (!current()) return;
            state.uploaded = true;
          }
        }
        if (!state.sealed) {
          setPhase(`Sealing target ${product.sourceTargetId}`);
          const sealed = data(await api.seal(delivery.deliveryId, product.sourceTargetId));
          if (!current()) return;
          if (
            !sealed.products.some(
              (item) => item.sourceTargetId === product.sourceTargetId && item.status === 'SEALED',
            )
          )
            throw new DeliveryRequestError(
              'The product is not sealed. Resume this product after checking staging.',
            );
          state.sealed = true;
        }
      }
      setPhase(`Validating target ${product.sourceTargetId}`);
      const report = data(
        await api.validate(delivery.deliveryId, product.sourceTargetId, state.requestId),
      );
      if (!current()) return;
      await assertDryRunReport(report, delivery.deliveryId, product.sourceTargetId);
      if (!current()) return;
      setReports((existing) => [...existing, report]);
      setPhase(`Target ${product.sourceTargetId} report recorded. Choose Next to continue.`);
    } catch (cause) {
      if (current())
        setError(
          cause instanceof DeliveryRequestError
            ? cause.message
            : 'This product stopped. Progress is retained; explicitly resume to retry the same operation.',
        );
    } finally {
      if (current()) setBusy(false);
    }
  };
  const refresh = async (report: ChecklistCatalogImpactReportDto) => {
    if (busy) return;
    const active = ++generation.current;
    setBusy(true);
    setError(null);
    try {
      const result = data(
        await api.report(
          report.report.deliveryId,
          report.report.sourceTargetId,
          report.report.reportId,
        ),
      );
      if (active !== generation.current) return;
      await assertDryRunReport(result, report.report.deliveryId, report.report.sourceTargetId);
      if (active !== generation.current) return;
      if (result.report.digest !== report.report.digest)
        throw new DeliveryRequestError(
          'The immutable report identity changed. Start a new preview.',
        );
      setReports((existing) =>
        existing.map((item) => (item.report.reportId === result.report.reportId ? result : item)),
      );
    } catch (cause) {
      if (active === generation.current)
        setError(
          cause instanceof DeliveryRequestError
            ? cause.message
            : 'Freshness could not be checked. The previous observation is retained.',
        );
    } finally {
      if (active === generation.current) setBusy(false);
    }
  };
  return (
    <section className="tcvs-card" aria-label="Catalog delivery dry run">
      <h3>Catalog delivery dry run</h3>
      <p>
        Stage immutable snapshots and compare one product at a time. Saved-card and
        selected-definition coverage is unknown, so every report remains blocked. This workflow
        cannot import or approve changes.
      </p>
      <p className="tcvs-muted">
        Admitted targets: 3557, 94, 8569, 12518, 228, 68585. JSON:{' '}
        {ZIP_LIMITS.maxMemberBytes / 1024 / 1024} MiB each. ZIP:{' '}
        {ZIP_LIMITS.maxArchiveBytes / 1024 / 1024} MiB, up to {ZIP_LIMITS.maxMembers} plain JSON
        members and {ZIP_LIMITS.maxExpandedBytes / 1024 / 1024} MiB expanded total. Staging uses 100
        requests/day and 30/minute, including baselines and report reads.
      </p>
      <label className="tcvs-field">
        <span>Snapshot JSON files or ZIP archives</span>
        <input
          key={inputVersion}
          type="file"
          accept=".json,.zip"
          multiple
          disabled={busy || ready}
          onChange={(event) => {
            const selected = Array.from(event.target.files ?? []);
            clear(false);
            setFiles(selected);
          }}
        />
      </label>
      <p className="tcvs-muted">
        Resume is available in this open page. Reloading or changing sessions clears local files and
        progress; staged bytes remain subject to their expiry. No background polling or automatic
        retries run.
      </p>
      {error && (
        <p className="tcvs-alert tcvs-alert-danger" role="alert">
          {error}
        </p>
      )}
      <p role="status">{phase}</p>
      <div className="tcvs-actions">
        {!ready && (
          <button type="button" disabled={busy || !files.length} onClick={() => void prepare()}>
            {manifest.current ? 'Resume preparation' : 'Prepare dry run'}
          </button>
        )}
        {ready && (
          <button
            type="button"
            disabled={busy || reports.length >= products.length}
            onClick={() => void next()}
          >
            {error ? 'Resume current product' : 'Validate next product'}
          </button>
        )}
        <button type="button" onClick={() => clear()}>
          Reset dry run
        </button>
        {reports.length > 0 && (
          <button
            type="button"
            onClick={() => downloadCatalogImpactReports(reports, 'catalog-dry-run.json')}
          >
            Download all reports (JSON)
          </button>
        )}
      </div>
      <p>
        {reports.length} of {products.length} product reports recorded
      </p>
      {reports.map((dto) => (
        <div key={dto.report.reportId} className="tcvs-card">
          <h4>Target {dto.report.sourceTargetId} · BLOCKED</h4>
          <p>
            Execution: {dto.report.execution} · Catalog comparison: {dto.report.catalogComparison} ·
            Verification: NOT_RUN
          </p>
          <p>
            Freshness: {dto.freshness.status} (checked {dto.freshness.checkedAt})
          </p>
          <p className="tcvs-mono">Report SHA-256: {dto.report.digest}</p>
          <p>
            Saved authoritative references, draft selections, review checklist selections and
            selected definitions: UNKNOWN; counts unavailable.
          </p>
          <p>
            Corrections: {dto.report.correctionImpact.coverage}; active{' '}
            {displayCount(dto.report.correctionImpact.active)}, replayed{' '}
            {displayCount(dto.report.correctionImpact.replayed)}, conflicts{' '}
            {displayCount(dto.report.correctionImpact.conflicts)}.
          </p>
          {(['providerDiff', 'effectiveDiff'] as const).map((kind) => (
            <div key={kind} style={{ overflowX: 'auto' }}>
              <h5>{kind === 'providerDiff' ? 'Provider comparison' : 'Effective comparison'}</h5>
              <table className="tcvs-table">
                <thead>
                  <tr>
                    <th>Records</th>
                    <th>Add</th>
                    <th>Update</th>
                    <th>Unchanged</th>
                    <th>Source missing (retained)</th>
                  </tr>
                </thead>
                <tbody>
                  {(['roster', 'parallelDefinitions', 'subsetDefinitions'] as const).map(
                    (records) => (
                      <tr key={records}>
                        <th scope="row">{records}</th>
                        {(['adds', 'updates', 'unchanged', 'sourceMissing'] as const).map(
                          (count) => (
                            <td key={count}>{displayCount(dto.report[kind][records][count])}</td>
                          ),
                        )}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          ))}
          <ul>
            {dto.report.reasons.map((reason, index) => (
              <li key={index}>
                {reason.code}: {reason.detail}
                {reason.fieldPath ? ` (${reason.fieldPath})` : ''}
              </li>
            ))}
          </ul>
          {dto.report.reasonsTruncated && (
            <p>
              Showing {dto.report.reasons.length} of {dto.report.totalReasonCount} reasons.
            </p>
          )}
          <button type="button" disabled={busy} onClick={() => void refresh(dto)}>
            Check current freshness for {dto.report.sourceTargetId}
          </button>
          <button
            type="button"
            onClick={() =>
              downloadCatalogImpactReports(
                [dto],
                `catalog-dry-run-${dto.report.sourceTargetId}.json`,
              )
            }
          >
            Download target {dto.report.sourceTargetId} report (JSON)
          </button>
        </div>
      ))}
    </section>
  );
}
function displayCount(value: number | null): string {
  return value === null ? 'Unavailable' : value.toLocaleString();
}
async function assertDryRunReport(
  dto: ChecklistCatalogImpactReportDto,
  deliveryId: string,
  sourceTargetId: string,
) {
  if (
    dto.report.deliveryId !== deliveryId ||
    dto.report.sourceTargetId !== sourceTargetId ||
    dto.report.policyVersion !== CHECKLIST_CATALOG_IMPACT_POLICY ||
    dto.report.coverage.savedReferences !== 'UNKNOWN' ||
    Object.values(dto.report.dependencyCoverage).some(
      (item) => item.coverage !== 'UNKNOWN' || item.count !== null,
    ) ||
    dto.report.eligibility !== 'BLOCKED' ||
    dto.report.applyAllowed !== false ||
    dto.report.verification !== 'NOT_RUN' ||
    !/^[a-f0-9]{64}$/.test(dto.report.digest) ||
    !(await verifyCatalogImpactReportDto(dto))
  )
    throw new DeliveryRequestError(
      'The dry-run report did not match this product. No report was accepted.',
    );
}
